/**
 * GlobalTasks — "My Tasks", redesigned (EM handoff): Board + List views,
 * status filter pills, quick-add, drag-drop between columns. Wired to real APIs.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "../../utils/toast";
import { Plus, LayoutGrid, List, MessageCircle, Clock } from "lucide-react";
import { wsApi } from "./wsApi";
import {
  TASK_STATUS,
  TASK_STATUS_ORDER,
  PRIORITY,
  fmtDate,
  fmtDuration,
} from "./wsConstants";
import { T, Avatar, PrimaryButton, StatusBadge, ConfirmDialog } from "../../components/em/ui";
import { Pills } from "./wsUi";

const PriorityTag = ({ priority }) => {
  const p = PRIORITY[priority] || PRIORITY.medium;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 6,
        background: p.bg,
        color: p.text,
        font: `600 10px ${T.font}`,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
        whiteSpace: "nowrap",
      }}
    >
      {p.label}
    </span>
  );
};

export default function GlobalTasks() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("board");
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [quick, setQuick] = useState("");
  const [TaskModal, setTaskModal] = useState(null);

  // Lazy-load the (heavy) TaskModal so this page stays light.
  useEffect(() => {
    import("./TaskModal").then((m) => setTaskModal(() => m.default));
  }, []);

  // `silent` skips the loading skeleton — used by the background refresh below,
  // which can fire while the task modal is open.
  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    wsApi
      .myTasks()
      .then(setTasks)
      .catch(() => toast.error("Could not load tasks"))
      .finally(() => { if (!silent) setLoading(false); });
  }, []);

  useEffect(() => {
    load();
    wsApi
      .listProjects()
      .then(setProjects)
      .catch(() => {});
    wsApi
      .users()
      .then(setUsers)
      .catch(() => {});
  }, [load]);

  // Time logged in the open TaskModal, or a live timer stopping in the header,
  // changes the duration shown on these rows — refetch without a skeleton.
  useEffect(() => {
    const handler = () => load(true);
    window.addEventListener("workspaceDataChanged", handler);
    return () => window.removeEventListener("workspaceDataChanged", handler);
  }, [load]);

  const quickAdd = async () => {
    if (!quick.trim()) return;
    try {
      await wsApi.createTask({ title: quick.trim() });
      setQuick("");
      load();
      toast.success("Task added");
    } catch {
      toast.error("Could not add task");
    }
  };

  const moveTask = async (id, status) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
    try {
      await wsApi.updateTaskStatus(id, status);
    } catch {
      toast.error("Could not move task");
      load();
    }
  };

  /* Drop highlight, painted straight onto the node rather than held in state.
     `TaskCard` is declared inside this component, so any state change hands
     React a new component identity and it remounts every card — pulling the
     element being dragged out of the DOM mid-drag, which can cancel the drag
     outright. Mutating style on the column costs no render at all, and matches
     how the cards already do their hover shadow below. */
  const paintColumn = (node, active) => {
    if (!node) return;
    node.style.background = active ? T.orange50 : T.bgMuted;
    node.style.borderColor = active ? T.orange : "transparent";
  };

  // Drop onto a column, wherever in it the pointer happens to be.
  const dropOnColumn = (e, status) => {
    e.preventDefault();
    paintColumn(e.currentTarget, false);
    const id = Number(e.dataTransfer.getData("text"));
    if (!id) return;
    // Dropping a task back into the column it came from is a no-op, not a
    // status change — without this it fires a PATCH and a re-render for
    // nothing every time someone picks a card up and puts it down.
    const current = tasks.find((t) => t.id === id);
    if (current && current.status === status) return;
    moveTask(id, status);
  };

  /* The MINIMUM column height: the rest of the viewport, so the empty space
     below the cards is still part of the column and still a drop target. A
     column with more cards than that grows past it and the page scrolls.

     Measured rather than hard-coded: a `calc(100vh - 460px)` would have to
     encode the height of the nav, title, quick-add and pills stacked above the
     board, and would go wrong the moment any of them wraps or a banner
     appears. The board's DOCUMENT offset is used (rect.top + scrollY), not its
     viewport offset — the latter changes as the page scrolls, which would
     resize the columns underneath a drag in progress. */
  const boardRef = useRef(null);
  const [colHeight, setColHeight] = useState(420);
  useEffect(() => {
    if (view !== "board") return undefined;
    const recalc = () => {
      const el = boardRef.current;
      if (!el) return;
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      // The floating dock is fixed over the bottom of the page, so leave room
      // for it — otherwise the foot of every column sits underneath it.
      setColHeight(Math.max(320, window.innerHeight - docTop - 104));
    };
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [view, loading]);

  const visible = tasks.filter((t) => {
    if (filter === "all") return true;
    return t.status === filter;
  });

  // Bulk selection (list view) — Team-list pattern.
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleRow = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allSelected =
    visible.length > 0 && visible.every((t) => selected.has(t.id));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(visible.map((t) => t.id)));

  const bulkStatus = async (status) => {
    const ids = Array.from(selected);
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);
    const results = await Promise.allSettled(
      ids.map((id) => wsApi.updateTaskStatus(id, status)),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    setBulkBusy(false);
    setSelected(new Set());
    load();
    if (failed)
      toast.error(`${failed} task${failed > 1 ? "s" : ""} could not be moved.`);
    else
      toast.success(
        `${ids.length} task${ids.length > 1 ? "s" : ""} moved to ${TASK_STATUS[status]?.label || status}`,
      );
  };

  const [confirmingBulk, setConfirmingBulk] = useState(false);

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);
    const results = await Promise.allSettled(
      ids.map((id) => wsApi.deleteTask(id)),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    setBulkBusy(false);
    setSelected(new Set());
    load();
    if (failed)
      toast.error(
        `${failed} task${failed > 1 ? "s" : ""} could not be deleted.`,
      );
    else
      toast.success(`${ids.length} task${ids.length > 1 ? "s" : ""} deleted`);
    setConfirmingBulk(false);
  };

  const FILTERS = [
    { value: "all", label: "All" },
    ...TASK_STATUS_ORDER.map((s) => ({
      value: s,
      label: TASK_STATUS[s].label,
    })),
  ];

  const TaskCard = ({ t }) => {
    const assignees = (t.members || []).filter(
      (m) => m.member_role === "assignee",
    );
    const overdue =
      t.due_date && new Date(t.due_date) < new Date() && t.status !== "done";
    return (
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData("text", String(t.id));
          // Gives the drag a "move" cursor rather than the default "copy".
          e.dataTransfer.effectAllowed = "move";
        }}
        onClick={() => setModal({ taskId: t.id })}
        style={{
          background: "var(--em-bg-card)",
          border: `1px solid ${T.border}`,
          borderRadius: 10,
          padding: "12px 13px",
          cursor: "pointer",
          boxShadow: T.shadowSm,
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.boxShadow =
            "0 4px 12px -4px rgba(10,10,10,0.15)")
        }
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = T.shadowSm)}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 8,
          }}
        >
          <PriorityTag priority={t.priority} />
          {t.project_name && (
            <span
              style={{
                font: `500 10px ${T.font}`,
                color: T.fg5,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t.project_name}
            </span>
          )}
        </div>
        <div
          style={{
            font: `500 13px/18px ${T.font}`,
            color: T.fg1,
            marginBottom: 10,
          }}
        >
          {t.title}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              font: `400 10px ${T.font}`,
              color: T.fg7,
            }}
          >
            {t.comment_count > 0 && (
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 3 }}
              >
                <MessageCircle size={11} />
                {t.comment_count}
              </span>
            )}
            {t.logged_minutes > 0 && (
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 3 }}
              >
                <Clock size={11} />
                {fmtDuration(t.logged_minutes)}
              </span>
            )}
            {t.due_date && (
              <span style={{ color: overdue ? T.danger : T.fg7 }}>
                {fmtDate(t.due_date)}
              </span>
            )}
          </div>
          <div style={{ display: "flex" }}>
            {assignees.slice(0, 3).map((m, i) => (
              <span
                key={m.user_id}
                style={{
                  marginLeft: i ? -7 : 0,
                  border: "2px solid #fff",
                  borderRadius: 9999,
                }}
              >
                <Avatar name={m.username} size={22} />
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Quick add */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input
          value={quick}
          onChange={(e) => setQuick(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && quickAdd()}
          placeholder="Quick add a task and press Enter…"
          style={{
            flex: 1,
            padding: "10px 13px",
            border: `1px solid ${T.border}`,
            borderRadius: T.rMd,
            font: `400 14px ${T.font}`,
            outline: "none",
          }}
        />
        <PrimaryButton icon={Plus} onClick={() => setModal({ create: true })}>
          New Task
        </PrimaryButton>
      </div>

      {/* Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <Pills options={FILTERS} value={filter} onChange={setFilter} />
        <div style={{ flex: 1 }} />
        <Pills
          options={[
            { value: "board", label: "Board", icon: LayoutGrid },
            { value: "list", label: "List", icon: List },
          ]}
          value={view}
          onChange={setView}
          style={{ borderRadius: 10 }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: T.fg7 }}>
          Loading…
        </div>
      ) : view === "board" ? (
        <div
          ref={boardRef}
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${TASK_STATUS_ORDER.length}, minmax(220px, 1fr))`,
            gap: 14,
            // `stretch`, not `start`: with `start` every column shrank to its
            // own content, so the space under the cards belonged to the page
            // rather than the column, and a drop there hit nothing.
            alignItems: "stretch",
            overflowX: "auto",
            paddingBottom: 8,
          }}
        >
          {TASK_STATUS_ORDER.map((s) => {
            const col = TASK_STATUS[s];
            const list = visible.filter((t) => t.status === s);
            return (
              <div
                key={s}
                onDragOver={(e) => {
                  // preventDefault on EVERY dragover is what marks the element
                  // a valid drop target — without it the browser rejects the
                  // drop and no `drop` event fires at all.
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  paintColumn(e.currentTarget, true);
                }}
                onDragLeave={(e) => {
                  // dragleave also fires when the pointer crosses into a child
                  // (the header, a card), so clear only once it has genuinely
                  // left the column — otherwise the highlight strobes.
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    paintColumn(e.currentTarget, false);
                  }
                }}
                onDrop={(e) => dropOnColumn(e, s)}
                style={{
                  background: T.bgMuted,
                  // Transparent when idle so the column doesn't shift by 2px
                  // when the highlight border appears.
                  border: "1px solid transparent",
                  borderRadius: 14,
                  padding: 10,
                  // A flex column whose card list takes the leftover space —
                  // that is what turns the empty area into droppable surface.
                  display: "flex",
                  flexDirection: "column",
                  // A FLOOR, not a fixed height. The viewport height is the
                  // minimum so a column with one card (or none) still offers a
                  // full-height drop target; past that the column grows with
                  // its cards and the page scrolls, rather than the cards
                  // scrolling inside a clipped column.
                  //
                  // `alignItems: stretch` on the grid then levels all four to
                  // the tallest, which is deliberate: unequal heights would
                  // shrink the quiet columns back to their content and take
                  // their drop area away again.
                  minHeight: colHeight,
                  transition: "background 0.12s ease, border-color 0.12s ease",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "4px 6px 10px",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 9999,
                      background: col.dot,
                    }}
                  />
                  <span style={{ font: `700 12px ${T.font}`, color: T.fg1 }}>
                    {col.label}
                  </span>
                  <span style={{ font: `600 11px ${T.font}`, color: T.fg7 }}>
                    {list.length}
                  </span>
                </div>
                {/* flex:1 so this list — not the page behind it — owns the
                    rest of the column. The drop handler is on the column, so
                    anywhere in here counts, cards or empty space alike.

                    No overflow of its own: the column grows to fit the cards
                    and the page scrolls. An `overflowY: auto` here would clip
                    a long list into an inner scrollbar, which hides the cards
                    below the fold and means a drag has to scroll two nested
                    surfaces to cross the board. */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    flex: 1,
                  }}
                >
                  {list.map((t) => (
                    <TaskCard key={t.id} t={t} />
                  ))}
                  {list.length === 0 && (
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 10,
                        border: `1px dashed ${T.border}`,
                        font: `500 11.5px ${T.font}`,
                        color: T.fg7,
                        textAlign: "center",
                        padding: 12,
                        // The column behind it owns the drop; this must not
                        // intercept the pointer or become a dragleave source.
                        pointerEvents: "none",
                      }}
                    >
                      Drop tasks here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          {selected.size > 0 && (
            <div className="em-bulk-bar">
              <span>{selected.size} selected</span>
              <div className="spacer" />
              <select
                disabled={bulkBusy}
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    bulkStatus(e.target.value);
                    e.target.value = "";
                  }
                }}
                style={{
                  height: 28,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(255,255,255,0.1)",
                  color: "#fff",
                  font: `600 12px ${T.font}`,
                  padding: "0 10px",
                }}
              >
                <option value="" disabled>
                  Move to…
                </option>
                {TASK_STATUS_ORDER.map((s) => (
                  <option key={s} value={s} style={{ color: "#0a0a0a" }}>
                    {TASK_STATUS[s].label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="danger"
                disabled={bulkBusy}
                onClick={() => setConfirmingBulk(true)}
              >
                Delete selected
              </button>
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => setSelected(new Set())}
              >
                Clear
              </button>
            </div>
          )}
          <div
            style={{
              background: "var(--em-bg-card)",
              border: `1px solid ${T.border}`,
              borderRadius: 14,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "0 16px",
                height: 38,
                background: T.bgHead,
                borderBottom: `1px solid ${T.border}`,
                font: `600 11px ${T.font}`,
                color: T.fg5,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              <div style={{ width: 30, flexShrink: 0 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  style={{ accentColor: "#f54b01" }}
                  aria-label="Select all tasks"
                />
              </div>
              <div style={{ flex: 1 }}>Task</div>
              <div style={{ width: 130, flexShrink: 0 }}>Project</div>
              <div style={{ width: 90, flexShrink: 0 }}>Priority</div>
              <div style={{ width: 110, flexShrink: 0 }}>Status</div>
              <div style={{ width: 80, flexShrink: 0, textAlign: "right" }}>
                Due
              </div>
            </div>
            {visible.length === 0 ? (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: T.fg7,
                  font: `400 13px ${T.font}`,
                }}
              >
                No tasks here.
              </div>
            ) : (
              visible.map((t, i) => (
                <div
                  key={t.id}
                  onClick={() => setModal({ taskId: t.id })}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "0 16px",
                    height: 52,
                    borderTop: i ? `1px solid ${T.divider}` : "none",
                    cursor: "pointer",
                    background: i % 2 ? T.bgZebra : "var(--em-bg-card)",
                  }}
                >
                  <div
                    style={{ width: 30, flexShrink: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggleRow(t.id)}
                      style={{ accentColor: "#f54b01" }}
                      aria-label={`Select ${t.title}`}
                    />
                  </div>
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      paddingRight: 8,
                      font: `500 13px ${T.font}`,
                      color: T.fg1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t.title}
                  </div>
                  <div
                    style={{
                      width: 130,
                      flexShrink: 0,
                      font: `500 11px ${T.font}`,
                      color: t.project_name ? T.fg5 : "#cbd5e1",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t.project_name || "Global"}
                  </div>
                  <div style={{ width: 90, flexShrink: 0 }}>
                    <PriorityTag priority={t.priority} />
                  </div>
                  <div style={{ width: 110, flexShrink: 0 }}>
                    <StatusBadge label={TASK_STATUS[t.status]?.label} />
                  </div>
                  <div
                    style={{
                      width: 80,
                      flexShrink: 0,
                      textAlign: "right",
                      font: `500 11px ${T.font}`,
                      color: T.fg5,
                    }}
                  >
                    {t.due_date ? fmtDate(t.due_date) : "—"}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {modal && TaskModal && (
        <TaskModal
          taskId={modal.taskId}
          projects={projects}
          users={users}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
          }}
        />
      )}

      <ConfirmDialog
        open={confirmingBulk}
        title={`Delete ${selected.size} task${selected.size > 1 ? "s" : ""}?`}
        message="Their comments and logged time go with them. This can't be undone."
        confirmLabel="Delete tasks"
        danger
        busy={bulkBusy}
        onConfirm={bulkDelete}
        onCancel={() => !bulkBusy && setConfirmingBulk(false)}
      />
    </div>
  );
}
